import { useCallback, useEffect, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { GoalCard } from './GoalCard';
import { GoalDialog, type GoalDraft } from './GoalDialog';
import { GoalTaskDialog, type GoalTaskDialogTarget, type GoalTaskDraft } from './GoalTaskDialog';
import { InstallJobPrompt } from './InstallJobPrompt';
import { GoalPhotosDialog } from './GoalPhotosDialog';
import {
  GOAL_STATUSES,
  type Car,
  type Goal,
  type GoalPhoto,
  type GoalTask,
  type GoalStatus,
  type Paginated,
  type SingleResponse,
} from '../../api/types';

/**
 * Garage section for a car's goals. Owner-only (the whole garage dashboard
 * is). Loads every goal once and filters by status client-side, so the
 * status tabs can show counts and tasks can be moved to any goal.
 */
interface GoalsSectionProps {
  car: Car;
  /** False for a follower reading someone else's garage — no edit controls. */
  canEdit?: boolean;
}

export function GoalsSection({ car, canEdit = true }: GoalsSectionProps) {
  const { t } = useTranslation();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState<GoalStatus>('active');
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | undefined>();
  const [taskTarget, setTaskTarget] = useState<GoalTaskDialogTarget | null>(null);
  const [boughtPart, setBoughtPart] = useState<GoalTask | null>(null);
  const [photosGoalId, setPhotosGoalId] = useState<number | null>(null);
  const photosGoal = goals.find((g) => g.id === photosGoalId) ?? null;

  useEffect(() => {
    apiFetch<Paginated<Goal>>(`/api/v1/cars/${car.id}/goals?per_page=100`)
      .then((res) => setGoals(res.data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [car.id]);

  const visibleGoals = useMemo(() => goals.filter((g) => g.status === status), [goals, status]);

  const countByStatus = useMemo(() => {
    const counts: Record<GoalStatus, number> = { active: 0, done: 0, dropped: 0 };
    goals.forEach((g) => {
      counts[g.status] += 1;
    });
    return counts;
  }, [goals]);

  const replaceGoal = (updated: Goal) =>
    setGoals((prev) => sortByPriority(prev.map((g) => (g.id === updated.id ? updated : g))));

  // Swallow-and-report wrapper for one-click actions (no dialog to show errors in).
  const run = useCallback(
    async (action: () => Promise<void>) => {
      setError('');
      try {
        await action();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : t('garageCar.goals.failed'));
      }
    },
    [t]
  );

  const openCreate = () => {
    setEditingGoal(undefined);
    setGoalDialogOpen(true);
  };

  const openEdit = (goal: Goal) => {
    setEditingGoal(goal);
    setGoalDialogOpen(true);
  };

  const saveGoal = async (draft: GoalDraft) => {
    if (editingGoal) {
      const res = await apiFetch<SingleResponse<Goal>>(`/api/v1/goals/${editingGoal.id}`, {
        method: 'PATCH',
        body: JSON.stringify(draft),
      });
      replaceGoal(res.data);
    } else {
      const res = await apiFetch<SingleResponse<Goal>>(`/api/v1/cars/${car.id}/goals`, {
        method: 'POST',
        body: JSON.stringify(draft),
      });
      setGoals((prev) => sortByPriority([res.data, ...prev]));
      setStatus('active');
    }
    setGoalDialogOpen(false);
  };

  const updateGoal = (goal: Goal, changes: Partial<Pick<Goal, 'priority' | 'deadline'>>) =>
    run(async () => {
      const res = await apiFetch<SingleResponse<Goal>>(`/api/v1/goals/${goal.id}`, {
        method: 'PATCH',
        body: JSON.stringify(changes),
      });
      replaceGoal(res.data);
    });

  const changeStatus = (goal: Goal, next: GoalStatus) =>
    run(async () => {
      const res = await apiFetch<SingleResponse<Goal>>(`/api/v1/goals/${goal.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: next }),
      });
      replaceGoal(res.data);
    });

  const deleteGoal = (goal: Goal) => {
    if (!window.confirm(t('garageCar.goals.deleteConfirm', { title: goal.title }))) return;
    run(async () => {
      await apiFetch(`/api/v1/goals/${goal.id}`, { method: 'DELETE' });
      setGoals((prev) => prev.filter((g) => g.id !== goal.id));
    });
  };

  const saveTask = async (target: GoalTaskDialogTarget, draft: GoalTaskDraft) => {
    if (target.mode === 'create') {
      const { goal_id: goalId, ...fields } = draft;
      const res = await apiFetch<SingleResponse<GoalTask>>(`/api/v1/goals/${goalId}/tasks`, {
        method: 'POST',
        body: JSON.stringify(fields),
      });
      setGoals((prev) =>
        prev.map((g) => (g.id === goalId ? { ...g, tasks: [...g.tasks, res.data] } : g))
      );
    } else {
      const { task } = target;
      const res = await apiFetch<SingleResponse<GoalTask>>(`/api/v1/goal-tasks/${task.id}`, {
        method: 'PATCH',
        body: JSON.stringify(draft),
      });
      const saved = res.data;
      setGoals((prev) =>
        prev.map((g) => {
          const without = g.tasks.filter((x) => x.id !== task.id);
          if (g.id !== saved.goal_id) return { ...g, tasks: without };
          // Same goal keeps its position; a moved task lands at the end.
          return g.id === task.goal_id
            ? { ...g, tasks: g.tasks.map((x) => (x.id === saved.id ? saved : x)) }
            : { ...g, tasks: [...without, saved] };
        })
      );
    }
    setTaskTarget(null);
  };

  const toggleTask = (task: GoalTask, completed: boolean) =>
    run(async () => {
      const res = await apiFetch<SingleResponse<GoalTask>>(`/api/v1/goal-tasks/${task.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: completed ? 'completed' : 'open' }),
      });
      const saved = res.data;
      setGoals((prev) =>
        prev.map((g) =>
          g.id === saved.goal_id
            ? { ...g, tasks: g.tasks.map((x) => (x.id === saved.id ? saved : x)) }
            : g
        )
      );
      if (completed && saved.type === 'buy') setBoughtPart(saved);
    });

  // Created straight away — price and note can be added later by editing it.
  const addInstallJob = (part: GoalTask) => {
    setBoughtPart(null);
    run(async () => {
      const res = await apiFetch<SingleResponse<GoalTask>>(`/api/v1/goals/${part.goal_id}/tasks`, {
        method: 'POST',
        body: JSON.stringify({
          type: 'do',
          title: t('garageCar.goals.installPrompt.jobTitle', { part: part.title }),
        }),
      });
      setGoals((prev) =>
        prev.map((g) => (g.id === part.goal_id ? { ...g, tasks: [...g.tasks, res.data] } : g))
      );
    });
  };

  const uploadPhotos = async (goal: Goal, files: File[]) => {
    const form = new FormData();
    files.forEach((file) => form.append('photos[]', file));
    const res = await apiFetch<SingleResponse<Goal>>(`/api/v1/goals/${goal.id}/photos`, {
      method: 'POST',
      body: form,
    });
    replaceGoal(res.data);
  };

  const setCoverPhoto = async (goal: Goal, photo: GoalPhoto) => {
    const res = await apiFetch<SingleResponse<Goal>>(`/api/v1/goals/${goal.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ cover_photo_id: photo.id }),
    });
    replaceGoal(res.data);
  };

  const deletePhoto = async (goal: Goal, photo: GoalPhoto) => {
    await apiFetch(`/api/v1/goals/${goal.id}/photos/${photo.id}`, { method: 'DELETE' });
    const photos = goal.photos.filter((p) => p.id !== photo.id);
    replaceGoal({ ...goal, photos, cover_photo_id: photos[0]?.id ?? null });
  };

  const deleteTask = (task: GoalTask) => {
    if (!window.confirm(t('garageCar.goals.tasks.deleteConfirm', { title: task.title }))) return;
    run(async () => {
      await apiFetch(`/api/v1/goal-tasks/${task.id}`, { method: 'DELETE' });
      setGoals((prev) =>
        prev.map((g) =>
          g.id === task.goal_id ? { ...g, tasks: g.tasks.filter((x) => x.id !== task.id) } : g
        )
      );
    });
  };

  return (
    <Stack spacing={3}>
      <SectionHeader
        title={t('garageCar.tabs.goals')}
        description={t('garageCar.goals.description')}
        action={
          canEdit ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              {t('garageCar.goals.newButton')}
            </Button>
          ) : undefined
        }
      />

      <ToggleButtonGroup
        exclusive
        size="small"
        value={status}
        onChange={(_, next: GoalStatus | null) => next && setStatus(next)}
      >
        {GOAL_STATUSES.map((s) => (
          <ToggleButton key={s} value={s}>
            {t(`garageCar.goals.filter.${s}`, { count: countByStatus[s] })}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : visibleGoals.length === 0 ? (
        <Typography color="text.secondary">{t(`garageCar.goals.empty.${status}`)}</Typography>
      ) : (
        <Stack spacing={2}>
          {visibleGoals.map((goal) => (
            <GoalCard
              key={goal.id}
              goal={goal}
              canEdit={canEdit}
              onEdit={() => openEdit(goal)}
              onUpdate={(changes) => updateGoal(goal, changes)}
              onOpenPhotos={() => setPhotosGoalId(goal.id)}
              onStatusChange={(next) => changeStatus(goal, next)}
              onDelete={() => deleteGoal(goal)}
              onAddTask={() => setTaskTarget({ mode: 'create', goal })}
              onToggleTask={toggleTask}
              onEditTask={(task) => setTaskTarget({ mode: 'edit', task })}
              onDeleteTask={deleteTask}
            />
          ))}
        </Stack>
      )}

      <GoalDialog
        open={goalDialogOpen}
        goal={editingGoal}
        onClose={() => setGoalDialogOpen(false)}
        onSubmit={saveGoal}
      />

      <GoalPhotosDialog
        goal={photosGoal}
        onClose={() => setPhotosGoalId(null)}
        onUpload={uploadPhotos}
        onSetCover={setCoverPhoto}
        onDelete={deletePhoto}
      />

      <InstallJobPrompt
        part={boughtPart}
        onDecline={() => setBoughtPart(null)}
        onAccept={addInstallJob}
      />

      <GoalTaskDialog
        target={taskTarget}
        goals={goals}
        onClose={() => setTaskTarget(null)}
        onSubmit={saveTask}
      />
    </Stack>
  );
}

const PRIORITY_RANK = { high: 0, medium: 1, low: 2 } as const;

/** Mirrors the API order: priority first, newest first within a priority. */
function sortByPriority(goals: Goal[]): Goal[] {
  return [...goals].sort(
    (a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] || b.id - a.id
  );
}
